import { createBrowserHistory } from 'history';
import { removeBasename, createURL } from '../path';
const PREVHISTORY_STACK_LIMIT = 10;
/**
 * Создает историю с поддержкой стека предыдущих состояний
 *
 * Расширяет стандартную history (browser/memory) следующими возможностями:
 * - Сохраняет стек предыдущих состояний в state.prev
 * - Поддерживает обработку basenames для виджета sdk
 * - Добавляет методы createURL и encodeLocation для поддержки HistoryRouter
 *
 * @param historyCreator - Функция создания истории (createBrowserHistory или createMemoryHistory)
 * @param role - Роль текущего модуля (используется для фильтрации путей в много-модульных приложениях)
 * @param basenames - Массив базовых путей модулей
 * @param basename - Базовый путь текущего модуля
 * @returns Функция, создающая расширенную историю
 *
 * @example
 * const history = createHistoryWidthPrev(createBrowserHistory)()
 */
export const createHistoryWidthPrev = (historyCreator, role, basenames, basename) => (...args) => {
    const historyInitiated = historyCreator(...args);
    const prevPush = historyInitiated.push;
    const prevStateStack = [];
    const pushToPrevHistoryStack = (prevState) => {
        if (prevStateStack.length === PREVHISTORY_STACK_LIMIT) {
            prevStateStack.pop();
        }
        prevStateStack.unshift(prevState);
    };
    const locationWithoutRole = (pathname) => {
        if (basenames &&
            basenames.length > 1 &&
            role &&
            pathname.includes(`/${role}`) &&
            basenames.some((moduleBasename, index) => !!index && pathname.startsWith(moduleBasename))) {
            return pathname.split(`/${role}`)[1];
        }
        return pathname;
    };
    historyInitiated.push = (location, state) => {
        let localBasename = '';
        if (basenames &&
            basenames.length > 1 &&
            historyInitiated.location.pathname === '/') {
            pushToPrevHistoryStack({
                pathname: basenames[0],
                search: historyInitiated.location.search,
            });
        }
        else {
            pushToPrevHistoryStack({
                pathname: historyInitiated.location.pathname,
                search: historyInitiated.location.search,
            });
        }
        if (typeof location === 'string') {
            if (basename) {
                localBasename = location.includes(basename)
                    ? ''
                    : basename.replace(/\/+$/, '');
            }
            location = locationWithoutRole(location);
            prevPush(`${localBasename}${removeBasename(location, basenames)}`, {
                prev: prevStateStack,
            });
        }
        else {
            if (location === null || location === void 0 ? void 0 : location.pathname) {
                if (basename) {
                    localBasename = location.pathname.includes(basename)
                        ? ''
                        : basename.replace(/\/+$/, '');
                }
                location.pathname = locationWithoutRole(location.pathname);
            }
            prevPush(Object.assign(Object.assign({}, location), (location.pathname
                ? {
                    pathname: `${localBasename}${removeBasename(location.pathname, basenames)}`,
                }
                : {})), Object.assign(Object.assign({}, Object.assign({}, state)), { prev: prevStateStack }));
        }
    };
    historyInitiated.createURL = createURL;
    historyInitiated.encodeLocation = (to) => {
        let url = createURL(to);
        return {
            pathname: url.pathname,
            search: url.search,
            hash: url.hash,
        };
    };
    return historyInitiated;
};
export const history = createHistoryWidthPrev(createBrowserHistory)();
/* eslint-disable-next-line @typescript-eslint/no-redeclare */
export const History = Symbol('History');
